import { JobApplicantStatus, JobStatus } from '@factoryfixinc/ats-interfaces';
import dayjs from 'dayjs';

import { jobApplicationFunnelOptions } from '@/core/conversations/pro-profile/utils/application-statuses';

/**
 * The Candidates pane's user-visible stage labels, keyed by status enum.
 * Single source for the pane's status-filter chips and the account-wide
 * search's stage pills. These deliberately keep the pane's long-standing copy
 * ("New", "Rejected") where it differs from the status picker's titles
 * ("New Application", "Reject") — do not rename without product sign-off.
 */
export const APPLICATION_STAGE_LABELS: Record<string, string> = {
  [JobApplicantStatus.CONTACT]: 'Sourcing',
  [JobApplicantStatus.NEW]: 'New',
  [JobApplicantStatus.CLIENT]: 'Engaged',
  [JobApplicantStatus.REVIEW]: 'Review',
  [JobApplicantStatus.INTERVIEW]: 'Interview',
  [JobApplicantStatus.OFFER]: 'Offer',
  [JobApplicantStatus.HIRED]: 'Hired',
  [JobApplicantStatus.REJECTED]: 'Rejected',
};

/**
 * Funnel membership and order come from the app's one shared status list
 * (`jobApplicationOptions`); only the label copy comes from the map above.
 */
export const APPLICATION_STATUS_FUNNEL: { status: JobApplicantStatus; label: string }[] =
  jobApplicationFunnelOptions.map(({ value, title }) => ({
    status: value,
    label: APPLICATION_STAGE_LABELS[value] ?? title,
  }));

/** Funnel order used to sort current-job search rows the way the pane does. */
export const APPLICATION_STATUS_FUNNEL_ORDER: string[] = APPLICATION_STATUS_FUNNEL.map(
  (option) => option.status,
);

/** Stage label for a search row — the pane's stage label, '' for no status. */
export function getStageLabel(status?: string | null): string {
  if (!status) return '';
  return APPLICATION_STAGE_LABELS[status] ?? status;
}

/**
 * Compact relative durations for the search results' timestamp rail
 * ("Applied 3mo ago", "Updated 20h ago"). The verb stays with the caller;
 * this only compacts the duration so every timestamp fits the fixed right
 * rail without stealing width from the truncating job title (ONE-6912).
 */
export function formatCompactTimeAgo(date: string | Date): string {
  const then = dayjs(date);
  if (!then.isValid()) return '';
  const now = dayjs();
  const minutes = Math.max(now.diff(then, 'minute'), 0);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = now.diff(then, 'hour');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(then, 'day');
  if (days < 30) return `${days}d ago`;
  const months = Math.max(now.diff(then, 'month'), 1);
  if (months < 12) return `${months}mo ago`;
  return `${Math.max(now.diff(then, 'year'), 1)}y ago`;
}

export interface JobStateTag {
  label: string;
  kind: 'archived' | 'closed' | 'draft' | 'review' | 'unavailable';
}

/**
 * Labels non-live jobs before selection so a jump to an archived, closed,
 * draft, or unavailable job is never a surprise (ONE-6912). Live jobs carry
 * no tag. Each label must stay truthful to where the jump actually lands:
 * draft and in-review jobs open fully editable, so calling either one a
 * "Closed job" would be a lie. Only REMOVED is truly gone ("Job unavailable");
 * ARCHIVED is filter-gated but still viewable.
 */
export function getJobStateTag(jobStatus?: string | null): JobStateTag | null {
  switch (jobStatus) {
    case JobStatus.LIVE:
    case undefined:
    case null:
    case '':
      return null;
    case JobStatus.ARCHIVED:
      return { label: 'Archived job', kind: 'archived' };
    case JobStatus.REMOVED:
      return { label: 'Job unavailable', kind: 'unavailable' };
    case JobStatus.DRAFT:
      return { label: 'Draft job', kind: 'draft' };
    case JobStatus.REVIEW:
      return { label: 'Job in review', kind: 'review' };
    default:
      // COMPLETE / EXPIRED / FILLED — genuinely closed postings.
      return { label: 'Closed job', kind: 'closed' };
  }
}
